import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, Circle, Download, Play, Plus, Square } from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import AddSensorForm from "./sensors-panel/AddSensorForm";
import SensorRow from "./sensors-panel/SensorRow";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  getSensorAttachType,
} from "./sensors-panel/sensorTaxonomy";
import {
  buildSensorRecordingCsv,
  buildSensorRecordingExport,
  downloadCsv,
  downloadJson,
  normalizeSensorMeta,
  timestampForFilename,
} from "./sensors-panel/sensorRecordingUtils";
import MutedText from "../ui/MutedText";
import Button from "../ui/Button";
import {
  useActuators,
  useBodies,
  useJoints,
  useSceneActions,
  useSensors,
  useSites,
  useTendons,
  useWorldBodies,
} from "../../stores/sceneStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { startSimulationFromCurrentFiles } from "../../physics/mujocoBridge";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import { buildBodyRootMap } from "../../utils/bodyHierarchy";

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

const findSceneRefByNameOrUuid = (refs, value) => {
  if (!refs || !value) return null;
  if (refs[value]) return refs[value];
  return Object.values(refs).find((ref) => ref?.uuid === value || ref?.name === value) ?? null;
};

const SensorsPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const sensors = useSensors() ?? EMPTY_OBJECT;
  const sites = useSites() ?? EMPTY_OBJECT;
  const joints = useJoints() ?? EMPTY_OBJECT;
  const actuators = useActuators() ?? EMPTY_OBJECT;
  const bodies = useBodies() ?? EMPTY_OBJECT;
  const tendons = useTendons() ?? EMPTY_OBJECT;
  const worldBodies = useWorldBodies() ?? EMPTY_ARRAY;
  const sceneActions = useSceneActions();
  const sensorMeta = usePhysicsStore((s) => s.sensorMeta);
  const physicsRunning = usePhysicsStore((s) => s.isRunning);
  const physicsStatus = usePhysicsStore((s) => s.status);
  const physicsTimestep = usePhysicsStore((s) => s.timestep);

  const [showAddForm, setShowAddForm] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingArmed, setRecordingArmed] = useState(false);
  const [startRecordingError, setStartRecordingError] = useState("");
  const [recordedCount, setRecordedCount] = useState(0);
  const samplesRef = useRef([]);
  const recordingRef = useRef(false);
  const recordingStartedAtRef = useRef(null);
  const recordingStoppedAtRef = useRef(null);
  const recordingSensorMetaRef = useRef(null);
  const lastRecordedTimeRef = useRef(null);
  const lastSampleUiUpdateRef = useRef(0);

  const grouped = useMemo(() => {
    const groups = {};
    Object.values(sensors).forEach((sensor) => {
      if (!sensor) return;
      const at = getSensorAttachType(sensor.kind);
      const groupKey = at ?? (sensor.kind?.startsWith("frame") ? "frame" : "other");
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(sensor);
    });
    Object.values(groups).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
    return groups;
  }, [sensors]);

  const totalCount = useMemo(() => Object.values(sensors).filter(Boolean).length, [sensors]);
  const sensorSceneRefs = useMemo(
    () => ({
      sites,
      joints,
      actuators,
      bodies,
      tendons,
      worldBodies,
      bodyRootMap: buildBodyRootMap(bodies, worldBodies),
    }),
    [actuators, bodies, joints, sites, tendons, worldBodies]
  );
  const runtimeSensors = useMemo(() => normalizeSensorMeta(sensorMeta), [sensorMeta]);
  const canRecordNow = physicsRunning;
  const canArmRecording = !isRecording && !recordingArmed;

  const handleAdd = useCallback(
    ({ kind, name, attrs }) => {
      sceneActions.addSensor({ kind, name, attrs });
      setShowAddForm(false);
    },
    [sceneActions]
  );

  const handleRemove = useCallback(
    (uuid) => {
      sceneActions.removeSensor(uuid);
    },
    [sceneActions]
  );

  const handleUpdateAttr = useCallback(
    (uuid, attrsPatch) => {
      sceneActions.updateSensor(uuid, attrsPatch);
    },
    [sceneActions]
  );

  const handleOpenAttachment = useCallback(
    ({ attachType, value }) => {
      if (!attachType || !value) return;
      const selectionActions = useSelectionStore.getState();
      selectionActions.setLastSelectionSource("sceneTree");

      if (attachType === "joint") {
        const joint = findSceneRefByNameOrUuid(joints, value);
        if (!joint?.uuid) return;
        selectionActions.selectJoint(joint.uuid);
      } else if (attachType === "site") {
        const site = findSceneRefByNameOrUuid(sites, value);
        if (!site?.uuid) return;
        selectionActions.selectSite(site.uuid);
      } else if (attachType === "actuator") {
        const actuator = findSceneRefByNameOrUuid(actuators, value);
        if (!actuator?.uuid) return;
        const parentJointUuid = resolveJointUuidFromRef(actuator?.joint, joints);
        const parentTendonUuid =
          actuator?.tendonUuid ?? findSceneRefByNameOrUuid(tendons, actuator?.tendon)?.uuid ?? null;
        selectionActions.selectActuatorInParent(
          actuator.uuid,
          parentJointUuid ?? actuator?.joint ?? null,
          parentTendonUuid
        );
      } else {
        return;
      }

      window.dispatchEvent(new CustomEvent("open-properties-panel"));
    },
    [actuators, joints, sites, tendons]
  );

  const beginRecording = useCallback(() => {
    const startedAt = new Date().toISOString();
    samplesRef.current = [];
    recordingRef.current = true;
    recordingStartedAtRef.current = startedAt;
    recordingStoppedAtRef.current = null;
    recordingSensorMetaRef.current = sensorMeta;
    lastRecordedTimeRef.current = null;
    lastSampleUiUpdateRef.current = 0;
    setRecordedCount(0);
    setIsRecording(true);
    setRecordingArmed(false);
    setStartRecordingError("");
  }, [sensorMeta]);

  const handleStartRecording = useCallback(async () => {
    if (!canArmRecording) return;
    setStartRecordingError("");

    if (canRecordNow) {
      beginRecording();
      return;
    }

    setRecordingArmed(true);
    try {
      if (!physicsRunning) {
        await startSimulationFromCurrentFiles();
      }
    } catch (error) {
      recordingRef.current = false;
      setRecordingArmed(false);
      setIsRecording(false);
      setStartRecordingError(String(error?.message || error || "Failed to start physics"));
    }
  }, [beginRecording, canArmRecording, canRecordNow, physicsRunning]);

  const handleStopRecording = useCallback(() => {
    if (!recordingRef.current) {
      setRecordingArmed(false);
      return;
    }
    const stoppedAt = new Date().toISOString();
    recordingRef.current = false;
    recordingStoppedAtRef.current = stoppedAt;
    setRecordingArmed(false);
    setRecordedCount(samplesRef.current.length);
    setIsRecording(false);
  }, []);

  const handleDownloadRecording = useCallback(() => {
    const samples = samplesRef.current;
    if (!samples.length) return;
    const stoppedAt = recordingStoppedAtRef.current || new Date().toISOString();
    const payload = buildSensorRecordingExport({
      sensorMeta: recordingSensorMetaRef.current || sensorMeta,
      samples,
      startedAt: recordingStartedAtRef.current,
      stoppedAt,
    });
    downloadJson(`mujoco-recording-${timestampForFilename()}.json`, payload);
  }, [sensorMeta]);

  const handleDownloadRecordingCsv = useCallback(() => {
    const samples = samplesRef.current;
    if (!samples.length) return;
    const csv = buildSensorRecordingCsv({
      sensorMeta: recordingSensorMetaRef.current || sensorMeta,
      samples,
    });
    downloadCsv(`mujoco-recording-${timestampForFilename()}.csv`, csv);
  }, [sensorMeta]);

  useEffect(() => {
    const onPoses = (e) => {
      if (!recordingRef.current) return;
      const sensordata = Array.isArray(e.detail?.sensordata) ? e.detail.sensordata : [];
      const qpos = Array.isArray(e.detail?.qpos) ? e.detail.qpos : [];
      const qvel = Array.isArray(e.detail?.qvel) ? e.detail.qvel : [];
      const ctrl = Array.isArray(e.detail?.ctrl) ? e.detail.ctrl : [];
      if (sensordata.length === 0 && qpos.length === 0 && qvel.length === 0 && ctrl.length === 0) {
        return;
      }
      const rawTime = Number(e.detail?.time);
      const nextSamples = samplesRef.current;
      const previousTime = lastRecordedTimeRef.current;
      const fallbackTime = Number.isFinite(previousTime)
        ? previousTime + (Number(physicsTimestep) || 0.002)
        : 0;
      const time =
        Number.isFinite(rawTime) && (!Number.isFinite(previousTime) || rawTime > previousTime)
          ? rawTime
          : fallbackTime;
      lastRecordedTimeRef.current = time;
      nextSamples.push({
        time,
        sensordata: sensordata.map((value) => Number(value)),
        qpos: qpos.map((value) => Number(value)),
        qvel: qvel.map((value) => Number(value)),
        ctrl: ctrl.map((value) => Number(value)),
      });

      const now = performance.now();
      if (now - lastSampleUiUpdateRef.current > 250) {
        lastSampleUiUpdateRef.current = now;
        setRecordedCount(nextSamples.length);
      }
    };
    window.addEventListener("physics-poses", onPoses);
    return () => window.removeEventListener("physics-poses", onPoses);
  }, [physicsTimestep]);

  useEffect(() => {
    if (recordingArmed && canRecordNow && !recordingRef.current) {
      beginRecording();
    }
  }, [beginRecording, canRecordNow, recordingArmed]);

  useEffect(() => {
    if (recordingArmed && physicsStatus === "error") {
      setRecordingArmed(false);
      setStartRecordingError("Physics failed to start.");
    }
  }, [physicsStatus, recordingArmed]);

  useEffect(() => {
    if (!physicsRunning && recordingRef.current) {
      handleStopRecording();
    }
  }, [physicsRunning, handleStopRecording]);

  return (
    <DraggablePanel
      title="Sensors"
      titleIcon={titleIcon}
      open={open}
      onClose={onClose}
      initialSize={{ width: 380, height: 400 }}
      minWidth={280}
      minHeight={220}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex h-full flex-col overflow-hidden text-sm text-gray-100">
        <div className="flex-shrink-0 border-b border-gray-700 px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold">
              Sensors <span className="text-gray-400">({totalCount})</span>
            </div>
            <button
              type="button"
              className="menu-button flex items-center gap-2 px-3 py-1 text-[10px] border border-gray-700 rounded hover:border-gray-600 transition-colors duration-150"
              onClick={() => setShowAddForm((v) => !v)}
              aria-label="New sensor"
            >
              <Plus className="w-3 h-3" aria-hidden />
              New Sensor
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {isRecording || recordingArmed ? (
              <Button
                variant="transparent"
                size="small"
                onClick={handleStopRecording}
                title={recordingArmed ? "Cancel armed sensor recording" : "Stop sensor recording"}
                className="gap-1.5 border-red-500/60 text-red-100 hover:border-red-400 hover:bg-red-950/30"
              >
                <Square className="h-3 w-3 fill-current" aria-hidden />
                {recordingArmed ? "Cancel" : "Stop"}
              </Button>
            ) : (
              <Button
                variant="transparent"
                size="small"
                onClick={handleStartRecording}
                disabled={!canArmRecording}
                title={
                  totalCount === 0
                    ? physicsRunning
                      ? "Record MuJoCo state data"
                      : "Start physics and record MuJoCo state data"
                    : physicsRunning && runtimeSensors.length === 0
                      ? "Record MuJoCo state data; runtime sensor metadata is unavailable"
                      : physicsRunning
                        ? "Record MuJoCo sensor and state data"
                        : "Start physics and record MuJoCo sensor and state data"
                }
                className="gap-1.5"
              >
                {physicsRunning ? (
                  <Circle className="h-3 w-3 fill-red-500 text-red-500" aria-hidden />
                ) : (
                  <Play className="h-3 w-3" aria-hidden />
                )}
                {recordingArmed
                  ? "Arming..."
                  : physicsRunning
                    ? "Record"
                    : "Start Physics & Record"}
              </Button>
            )}
            <Button
              variant="transparent"
              size="small"
              onClick={handleDownloadRecording}
              disabled={isRecording || recordedCount === 0}
              title="Download recorded sensor and state data as JSON"
              className="gap-1.5"
            >
              <Download className="h-3 w-3" aria-hidden />
              JSON
            </Button>
            <Button
              variant="transparent"
              size="small"
              onClick={handleDownloadRecordingCsv}
              disabled={isRecording || recordedCount === 0}
              title="Download recorded sensor and state data as CSV"
              className="gap-1.5"
            >
              <Download className="h-3 w-3" aria-hidden />
              CSV
            </Button>
            <span className="text-[10px] panel-text-helper">
              {isRecording ? "recording" : recordedCount > 0 ? "ready" : physicsStatus}
              {" | "}
              {recordedCount} samples
              {recordingArmed ? " | waiting for physics" : ""}
              {runtimeSensors.length > 0 ? ` | ${runtimeSensors.length} runtime sensors` : ""}
            </span>
          </div>
          {startRecordingError ? (
            <div className="mt-1 text-[11px] text-red-300">{startRecordingError}</div>
          ) : null}
        </div>

        {showAddForm && (
          <div className="shrink-0 border-b border-gray-700 py-2">
            <AddSensorForm
              sites={sites}
              joints={joints}
              actuators={actuators}
              bodies={bodies}
              tendons={tendons}
              worldBodies={worldBodies}
              bodyRootMap={sensorSceneRefs.bodyRootMap}
              onAdd={handleAdd}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}

        {totalCount === 0 && !showAddForm ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-8">
            <Activity className="h-8 w-8 panel-text-disabled" />
            <MutedText className="text-center text-sm">No Sensors Detected</MutedText>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-1 py-1">
            {CATEGORY_ORDER.map((key) => {
              const group = grouped[key];
              if (!group?.length) return null;
              return (
                <div key={key} className="mb-2">
                  <div className="mb-0.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-200">
                    {CATEGORY_LABELS[key]}{" "}
                    <span className="font-medium tabular-nums text-gray-400">({group.length})</span>
                  </div>
                  {group.map((sensor) => (
                    <SensorRow
                      key={sensor.uuid}
                      sensor={sensor}
                      sites={sites}
                      joints={joints}
                      actuators={actuators}
                      bodies={bodies}
                      tendons={tendons}
                      worldBodies={worldBodies}
                      bodyRootMap={sensorSceneRefs.bodyRootMap}
                      onRemove={handleRemove}
                      onUpdateAttr={handleUpdateAttr}
                      onOpenAttachment={handleOpenAttachment}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DraggablePanel>
  );
};

export default React.memo(SensorsPanel);
